import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { fetchProducts } from "../api";
import { useSEO } from "../hooks/useSEO";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { MENU_GROUPS } from "../config/brand";

const SIZES = ["M", "L", "XL"];
const SORTS = [
  { value: "featured", label: "FEATURED" },
  { value: "newest", label: "NEWEST" },
  { value: "price_asc", label: "PRICE: LOW TO HIGH" },
  { value: "price_desc", label: "PRICE: HIGH TO LOW" },
];

const chip = "bg-[#6f6f6f] text-white px-1.5 py-0.5 text-[13px] md:text-[15px] tracking-[0.02em]";

/* Paly-style breadcrumb: [GROUP] -> [SIBLING / SIBLING / CURRENT] */
const Crumbs = ({ category, title }) => {
  const group = MENU_GROUPS.find((g) => g.links.some((l) => l.to === `/${category}`));
  const links = group ? group.links : MENU_GROUPS.flatMap((g) => g.links);
  return (
    <div className="flex items-center gap-2 min-w-0 overflow-x-auto no-scrollbar" data-testid="collection-breadcrumb">
      <Link to="/all-products" className={`${chip} flex-shrink-0 hover:bg-black transition-colors`}>
        {group ? group.title : "SHOP"}
      </Link>
      <span className="text-[13px] md:text-[15px] flex-shrink-0" aria-hidden="true">→</span>
      <div className={`${chip} flex items-center gap-1.5 whitespace-nowrap`}>
        {links.map((l, i) => {
          const active = l.to === `/${category}` || (!category && l.to === "/all-products");
          return (
            <span key={l.to} className="flex items-center gap-1.5">
              <Link
                to={l.to}
                data-testid={`crumb-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`hover:underline underline-offset-[3px] ${active ? "underline" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {l.label}
              </Link>
              {i < links.length - 1 && <span aria-hidden="true">/</span>}
            </span>
          );
        })}
        {!category && <span className="underline">{title}</span>}
      </div>
    </div>
  );
};

export default function CollectionPage({ category, title }) {
  const [products, setProducts] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const panelRef = useRef(null);

  const availability = params.get("availability") || "";
  const size = params.get("size") || "";
  const sort = params.get("sort") || "featured";

  useSEO(
    `${title} — RICH KIDS FUNERAL`,
    `Shop ${title.toLowerCase()} — RICH KIDS FUNERAL First Collection 26.`
  );

  useEffect(() => {
    setProducts(null);
    const q = { sort };
    if (category) q.category = category;
    if (availability) q.availability = availability;
    if (size) q.size = size;
    fetchProducts(q).then(setProducts).catch(() => setProducts([]));
  }, [category, availability, size, sort]);

  useEffect(() => {
    if (!filtersOpen) return;
    const close = (e) => !panelRef.current?.contains(e.target) && setFiltersOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [filtersOpen]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const opt = (active) =>
    `text-[12px] tracking-[0.08em] px-3 py-1.5 border transition-colors ${
      active ? "bg-black text-white border-black" : "border-black/20 hover:border-black"
    }`;

  return (
    <div className="px-3 md:px-6 pt-5 md:pt-6" data-testid={`collection-page-${category || "all"}`}>
      <h1 className="sr-only" data-testid="collection-title">{title}</h1>

      {/* breadcrumb + FILTERS dropdown */}
      <div className="flex items-center justify-between gap-4">
        <Crumbs category={category} title={title} />
        <div className="relative flex-shrink-0" ref={panelRef}>
          <button
            data-testid="filters-toggle"
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex items-center gap-1.5 text-[13px] md:text-[15px] tracking-[0.02em] hover:opacity-50 transition-opacity"
            aria-expanded={filtersOpen}
          >
            FILTERS
            <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? "rotate-180" : ""}`} strokeWidth={2} />
          </button>
          <AnimatePresence>
            {filtersOpen && (
              <motion.div
                data-testid="filter-panel"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 top-full mt-3 z-30 bg-white border border-black p-5 w-[280px] space-y-5"
              >
                <div>
                  <p className="text-[10px] tracking-[0.25em] text-black/40 mb-2">SORT</p>
                  <div className="flex flex-wrap gap-2">
                    {SORTS.map((s) => (
                      <button
                        key={s.value}
                        data-testid={`sort-${s.value}`}
                        onClick={() => setParam("sort", s.value === "featured" ? "" : s.value)}
                        className={opt(sort === s.value)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] text-black/40 mb-2">AVAILABILITY</p>
                  <div className="flex gap-2">
                    {[
                      { v: "in_stock", l: "IN STOCK" },
                      { v: "sold_out", l: "SOLD OUT" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        data-testid={`filter-availability-${o.v}`}
                        onClick={() => setParam("availability", availability === o.v ? "" : o.v)}
                        className={opt(availability === o.v)}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] text-black/40 mb-2">SIZE</p>
                  <div className="flex flex-wrap gap-2">
                    {SIZES.map((s) => (
                      <button
                        key={s}
                        data-testid={`filter-size-${s.toLowerCase()}`}
                        onClick={() => setParam("size", size === s ? "" : s)}
                        className={opt(size === s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {(availability || size || sort !== "featured") && (
                  <button
                    data-testid="filters-clear"
                    onClick={() => setParams({}, { replace: true })}
                    className="text-[10px] tracking-[0.2em] underline underline-offset-4 hover:opacity-50 transition-opacity"
                  >
                    CLEAR ALL
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* grid */}
      {products === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 md:gap-x-5 gap-y-12 mt-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="aspect-[1/1] bg-[#fafafa] animate-pulse" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <p className="text-[11px] tracking-[0.18em] text-black/50 mt-16" data-testid="collection-empty">
          NO PRODUCTS FOUND
        </p>
      ) : (
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 md:gap-x-5 gap-y-12 mt-8"
          data-testid="collection-grid"
        >
          {products.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.04, 0.3)}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
